import type { CalendarBlock } from './buildDayBlocks'

/**
 * May this block be dragged, and if not, why?
 *
 * The worker-side counterpart of `disposition-board`'s `resolveDragPolicy`, and deliberately much
 * narrower than it. A dispatcher moves anybody's anything; a Handwerker may only move the two
 * things that are theirs to move — their Pause, and their own Privatzeit. Krankheit, Urlaub and
 * Schulung are set by a manager and are read-only here, which is why `category` is checked at all.
 *
 * It replaces a predicate that was inlined in `DayColumn`'s JSX, where it could not be reused by
 * the drop handler and could not say *why* something was inert.
 */

export type CalendarDragBlocker =
  | 'not-draggable-kind'
  | 'not-own'
  | 'not-personal-leave'
  | 'all-day'
  | 'flagged'
  | 'multi-technician'
  | 'clipped'

export interface CalendarDragPolicy {
  canDrag: boolean
  blockedBy: CalendarDragBlocker | null
}

const ALLOWED: CalendarDragPolicy = { canDrag: true, blockedBy: null }
const blocked = (blockedBy: CalendarDragBlocker): CalendarDragPolicy => ({
  canDrag: false,
  blockedBy,
})

export function resolveCalendarPolicy(
  block: CalendarBlock,
  ctx: { currentUserId: number },
): CalendarDragPolicy {
  switch (block.kind) {
    /*
     * Always movable, including a break the server merely projected (`entry.id === null`). It has
     * no row yet, but `PUT /schedule/break` is an upsert keyed by technician and day, so dragging
     * one is what materialises it. Same rule as the board.
     */
    case 'BREAK':
      return ALLOWED

    case 'ABSENCE': {
      const entry = block.entry
      if (!entry || entry.id == null) return blocked('not-own')
      if (entry.technicianId !== ctx.currentUserId) return blocked('not-own')
      // Manager-owned categories stay read-only for the technician they are about.
      if (entry.category !== 'PERSONAL_LEAVE') return blocked('not-personal-leave')
      if (entry.needsRescheduling) return blocked('flagged')
      // Nothing to drag it *to*: it already fills the working day.
      if (block.coversWholeShift) return blocked('all-day')
      /*
       * One absence, several technicians, one parent row. `PATCH` moves the parent, so dragging
       * would silently move a colleague's day too — the board refuses this for the same reason.
       */
      if (block.technicianCount > 1) return blocked('multi-technician')
      // Only part of it is on screen, so a delta measured here would misplace the rest.
      if (block.span.clippedStart || block.span.clippedEnd) return blocked('clipped')
      return ALLOWED
    }

    // Rescheduling an appointment or a task is a dispatcher's decision. Tapping opens the detail.
    case 'APPOINTMENT':
    case 'TASK':
    default:
      return blocked('not-draggable-kind')
  }
}
